import { b as buildPaginationParams, n as normalizeToPaginated } from './chunks/pagination-utils-9vQ-Npkr.js';

/**
 * Platform-Agnostic User Status API Client
 *
 * Handles user status operations using the PERS backend.
 * Matches framework UserStatusApiService methods exactly.
 *
 * ✅ UPDATED: All endpoints updated to new RESTful /users patterns
 *
 * Error handling patterns follow framework implementation:
 * - getRemoteEarnedUserStatus() uses silent fallback (empty array)
 * - Other operations throw errors for proper error handling
 */
class UserStatusApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/users';
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * PUBLIC: Get remote user status types
     * ✅ UPDATED: /user/status-type → /users/status-types
     */
    async getRemoteUserStatusTypes(options) {
        try {
            const params = buildPaginationParams(options);
            const response = await this.apiClient.get(`${this.basePath}/status-types?${params.toString()}`);
            return normalizeToPaginated(response);
        }
        catch (error) {
            console.error('Error getting user status types', error);
            throw error;
        }
    }
    // ==========================================
    // AUTHENTICATED OPERATIONS
    // ==========================================
    /**
     * AUTH: Get earned user status for authenticated user
     * ✅ UPDATED: /user/auth/status → /users/me/status
     * ✅ FIXED: Returns PaginatedResponseDTO<UserStatusTypeDTO> to match v2.0.0
     * Note: Uses silent fallback pattern from framework - returns empty paginated response on error
     */
    async getRemoteEarnedUserStatus(options) {
        try {
            const params = buildPaginationParams(options);
            const response = await this.apiClient.get(`${this.basePath}/me/status?${params.toString()}`);
            return normalizeToPaginated(response);
        }
        catch (error) {
            console.error('Error getting user status', error);
            // ✅ FIXED: Silent fallback pattern - return empty paginated response
            return {
                data: [],
                pagination: { page: 1, limit: 20, total: 0, pages: 0, hasNext: false, hasPrev: false }
            };
        }
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * ADMIN: Create user status type
     * ✅ UPDATED: /user/admin/status-type → /users/status-types
     */
    async createUserStatusType(userStatusType) {
        try {
            return await this.apiClient.post(`${this.basePath}/status-types`, userStatusType);
        }
        catch (error) {
            console.error('Error creating user status type', error);
            throw error;
        }
    }
    /**
     * ADMIN: Update user status type
     * PUT /users/status-types/:id
     */
    async updateUserStatusType(id, userStatusType) {
        try {
            return await this.apiClient.put(`${this.basePath}/status-types/${id}`, userStatusType);
        }
        catch (error) {
            console.error('Error updating user status type', error);
            throw error;
        }
    }
    /**
     * ADMIN: Delete user status type
     * DELETE /users/status-types/:id
     */
    async deleteUserStatusType(id) {
        try {
            await this.apiClient.delete(`${this.basePath}/status-types/${id}`);
        }
        catch (error) {
            console.error('Error deleting user status type', error);
            throw error;
        }
    }
}

/**
 * Platform-Agnostic User Status Service
 *
 * Contains user status business logic and operations that work across platforms.
 * No framework dependencies - pure TypeScript business logic.
 * Matches framework UserStatusApiService capabilities exactly.
 */
class UserStatusService {
    constructor(userStatusApi) {
        this.userStatusApi = userStatusApi;
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * PUBLIC: Get remote user status types
     */
    async getRemoteUserStatusTypes(options) {
        return this.userStatusApi.getRemoteUserStatusTypes(options);
    }
    // ==========================================
    // AUTHENTICATED OPERATIONS
    // ==========================================
    /**
     * AUTH: Get earned user status for authenticated user
     */
    async getRemoteEarnedUserStatus(options) {
        return this.userStatusApi.getRemoteEarnedUserStatus(options);
    }
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * ADMIN: Create user status type
     */
    async createUserStatusType(userStatusType) {
        return this.userStatusApi.createUserStatusType(userStatusType);
    }
    /**
     * ADMIN: Update user status type
     */
    async updateUserStatusType(id, userStatusType) {
        return this.userStatusApi.updateUserStatusType(id, userStatusType);
    }
    /**
     * ADMIN: Delete user status type
     */
    async deleteUserStatusType(id) {
        return this.userStatusApi.deleteUserStatusType(id);
    }
}

/**
 * @explorins/pers-sdk-user-status
 *
 * Platform-agnostic User Status Domain SDK for PERS ecosystem
 * Handles user status management and type operations
 */
// API Layer
/**
 * Create a complete User Status SDK instance
 *
 * @param apiClient - Configured PERS API client
 * @returns User Status SDK with flattened structure for better DX
 */
function createUserStatusSDK(apiClient) {
    const userStatusApi = new UserStatusApi(apiClient);
    const userStatusService = new UserStatusService(userStatusApi);
    return {
        // Direct access to service methods (primary interface)
        // ✅ FRAMEWORK ALIGNED: Only methods actually used by framework
        // Public methods
        getRemoteUserStatusTypes: (options) => userStatusService.getRemoteUserStatusTypes(options),
        // Auth methods
        getRemoteEarnedUserStatus: (options) => userStatusService.getRemoteEarnedUserStatus(options),
        // Admin methods
        createUserStatusType: (userStatusType) => userStatusService.createUserStatusType(userStatusType),
        updateUserStatusType: (id, userStatusType) => userStatusService.updateUserStatusType(id, userStatusType),
        deleteUserStatusType: (id) => userStatusService.deleteUserStatusType(id),
        // Advanced access for edge cases
        api: userStatusApi,
        service: userStatusService
    };
}

export { UserStatusApi, UserStatusService, createUserStatusSDK };
//# sourceMappingURL=user-status.js.map
